import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as d3 from 'd3';
import { sankey, sankeyLinkHorizontal, sankeyLeft, sankeyRight, sankeyCenter, sankeyJustify } from 'd3-sankey';
import { KeyGoal } from '../../types';

interface ProjectItem {
    name: string;
    overview_and_goals: string;
    presidio_controlled?: string;
    estimated_completion?: string;
    it_initiative_alignment?: string;
    modernization_plan_project?: string;
    project_id?: string;
    goals?: KeyGoal[];
    presidio_areas?: string[];
}

interface SankeyNodeExtra {
    id: number;
    name: string;
    column: number;
    // Properties added by D3 Sankey
    x0?: number;
    x1?: number;
    y0?: number;
    y1?: number;
    sourceLinks?: any[];
    targetLinks?: any[];
    depth?: number;
}

interface SankeyLinkExtra {
    source: number | SankeyNodeExtra;
    target: number | SankeyNodeExtra;
    value: number;
    mpp?: string; // "Yes" for modernization projects, "No" for regular projects
    projectName?: string; // Add project name to track which project this link belongs to
    goalName?: string; // Business goal that owns this flow
}

const columnTitles = ['Goals', 'Projects', 'Practices', 'Sub-Practices', 'Technologies'];
const DASHBOARD_MAP_MIN_WIDTH = 1400;
const DEFAULT_MAP_MIN_WIDTH = 800;

interface OutputGraphProps {
    projects?: any[];
    hideCheckbox?: boolean;
    hideControls?: boolean;
    customerName?: string;
    /** When provided, overrides internal state for presentation mode */
    showModernizationProjects?: boolean;
    /** When set, use instead of "Project Output" for the h1 title */
    customTitle?: string;
    /** When true, hide the description paragraph */
    hideDescription?: boolean;
    /** When true, hide the graph title/header row */
    hideHeader?: boolean;
    /** When true, use overflow-hidden to prevent scrollbars in presentation */
    presentationMode?: boolean;
    /** Use dashboard map-style buttons instead of the original control panel */
    controlsVariant?: 'default' | 'mapButtons';
    /** Override the responsive map minimum width for presentation/export contexts */
    mapMinWidth?: number;
    /** Override the lowest responsive map height */
    mapMinHeight?: number;
    /** Override the tallest responsive map height */
    mapMaxHeight?: number;
    /** Ratio used to derive map height from width */
    mapHeightRatio?: number;
    /** Optional action to render in the header row (e.g. Download as PNG button) */
    headerAction?: React.ReactNode;
}

const OutputGraph: React.FC<OutputGraphProps> = ({ projects = [], hideCheckbox = false, hideControls = false, customerName = "Customer", showModernizationProjects: showModernizationProp, customTitle, hideDescription = false, hideHeader = false, presentationMode = false, controlsVariant = 'default', mapMinWidth, mapMinHeight = 600, mapMaxHeight = 760, mapHeightRatio = 0.42, headerAction }) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [internalShowModernization, setInternalShowModernization] = useState(false);
    const showModernizationProjects = showModernizationProp ?? internalShowModernization;
    const effectiveMapMinWidth = mapMinWidth ?? (controlsVariant === 'mapButtons' ? DASHBOARD_MAP_MIN_WIDTH : DEFAULT_MAP_MIN_WIDTH);
    const [dimensions, setDimensions] = useState({
        width: controlsVariant === 'mapButtons' ? effectiveMapMinWidth : Math.max(1200, effectiveMapMinWidth),
        height: mapMinHeight
    });
    const [highlightedPaths, setHighlightedPaths] = useState<Set<string>>(new Set());
    const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
    
    // New state variables for controls (matching Vue implementation)
    const [linkColor, setLinkColor] = useState<'source' | 'source-target' | 'static' | 'target'>('source');
    const [nodeAlign, setNodeAlign] = useState<'justify' | 'left' | 'right' | 'center'>('justify');
    
    // Color palette matching Vue implementation
    const colorPalette = useMemo(() => [
        "#264653", "#2A9D8F", "#E9C46A", "#F4A261", "#E76F51",
        "#6D597A", "#B56576", "#E56B6F", "#EAAC8B", "#FFE5B4",
        "#8ECAE6", "#219EBC", "#023047", "#FFB703", "#FB8500",
        "#A7C957", "#6A994E", "#386641", "#BC4749", "#F7B801",
        "#3D5A80", "#98C1D9", "#EE6C4D", "#E0FBFC", "#FFDDD2",
        "#B5838D", "#6D6875", "#FFCDB2", "#FFB4A2", "#CDB4DB"
    ], []);

    // Calculate responsive dimensions
    useEffect(() => {
        const updateDimensions = () => {
            if (!containerRef.current) return;

            const styles = window.getComputedStyle(containerRef.current);
            const horizontalPadding = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight);
            const contentWidth = Math.max(containerRef.current.clientWidth - horizontalPadding, 0);
            const responsiveWidth = Math.max(contentWidth, effectiveMapMinWidth);
            const responsiveHeight = Math.max(mapMinHeight, Math.min(mapMaxHeight, Math.round(responsiveWidth * mapHeightRatio)));

            setDimensions((prev) => {
                if (prev.width === responsiveWidth && prev.height === responsiveHeight) {
                    return prev;
                }
                return { width: responsiveWidth, height: responsiveHeight };
            });
        };

        updateDimensions();
        const observer = new ResizeObserver(updateDimensions);
        if (containerRef.current) {
            observer.observe(containerRef.current);
        }
        window.addEventListener('resize', updateDimensions);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateDimensions);
        };
    }, [controlsVariant, effectiveMapMinWidth, mapHeightRatio, mapMaxHeight, mapMinHeight]);

    const hasDisplayableData = useMemo(() => {
        if (!projects?.length) return false;
        const isModernizationProject = (p: ProjectItem) =>
            p.modernization_plan_project === 'Yes' || (p as any).Modernization_plan_project === 'Yes';
        if (showModernizationProjects) {
            return projects.some(isModernizationProject);
        }
        return projects.some(p => !isModernizationProject(p));
    }, [projects, showModernizationProjects]);

    const resetHighlighting = useCallback(() => {
        setHighlightedPaths(new Set());

        const svg = svgRef.current ? d3.select(svgRef.current) : null;
        if (!svg) return;

        svg.selectAll<SVGPathElement, any>('.sankey-link')
            .style('opacity', null)
            .style('filter', 'none')
            .attr('stroke-opacity', (d: any) => {
                if (d.mpp === 'Yes') {
                    return showModernizationProjects ? 0.2 : 0;
                }
                return 0.7;
            })
            .attr('stroke-width', (d: any) => {
                if (d.mpp === 'Yes') {
                    return showModernizationProjects ? Math.max(1, d.width ?? 1) : 0;
                }
                return Math.max(1, d.width ?? 1);
            });

        svg.selectAll<SVGGElement, any>('.sankey-node')
            .style('opacity', 1)
            .style('filter', 'none');
    }, [showModernizationProjects]);

    const clampScale = (scale: number) => Math.max(0.55, Math.min(1.8, scale));
    const zoomBy = (factor: number) => {
        setView((prev) => ({ ...prev, scale: clampScale(prev.scale * factor) }));
    };
    const resetView = () => setView({ scale: 1, x: 0, y: 0 });
    const fitToView = () => resetView();
    const toggleModernizationProjects = () => {
        if (showModernizationProp === undefined) {
            setInternalShowModernization((prev) => !prev);
        }
    };

    // Create the Sankey diagram - moved before useEffects to fix dependency order
    const createSankeyDiagram = useCallback((nodes: SankeyNodeExtra[], links: SankeyLinkExtra[], showModernizationProjects: boolean, modernizationProjects: Set<string>) => {
        if (!svgRef.current) return;

        // Set up dimensions
        const width = dimensions.width;
        const height = dimensions.height;
        // Adjust margins for smaller screens
        const margin = { 
            top: 45, 
            right: width < 1000 ? 20 : 30, 
            bottom: 30, 
            left: width < 1000 ? 20 : 30 
        };
        const innerWidth = width - margin.left - margin.right;
        const innerHeight = height - margin.top - margin.bottom;

        // Create SVG
        const svg = d3.select(svgRef.current)
            .attr('width', width)
            .attr('height', height)
            .attr('viewBox', `0 0 ${width} ${height}`)
            .attr('preserveAspectRatio', 'xMidYMid meet');

        // Add background rectangle for click outside functionality
        svg.append('rect')
            .attr('width', width)
            .attr('height', height)
            .attr('fill', 'transparent')
            .attr('pointer-events', 'all')
            .style('cursor', 'default')
            .on('click', function(event: any) {
                if (event.target === this) {
                    resetHighlighting();
                }
            });

        const g = svg.append('g')
            .attr('transform', `translate(${margin.left + view.x},${margin.top + view.y}) scale(${view.scale})`);

        // Set up the Sankey generator with responsive settings
        const nodeWidth = width < 1000 ? 12 : 15;
        const nodePadding = width < 1000 ? 8 : 10;
        
        
        // Convert nodeAlign from a name to a function (exactly like Vue)
        let nodeAlignFunction;
        if (controlsVariant === 'mapButtons') {
            nodeAlignFunction = (node: any) => node.column;
        } else if (typeof nodeAlign === "function") {
            nodeAlignFunction = nodeAlign;
        } else {
            // Map string values to actual D3 Sankey alignment functions (like Vue)
            switch (nodeAlign) {
                case 'left':
                    nodeAlignFunction = sankeyLeft;
                    break;
                case 'right':
                    nodeAlignFunction = sankeyRight;
                    break;
                case 'center':
                    nodeAlignFunction = sankeyCenter;
                    break;
                case 'justify':
                default:
                    nodeAlignFunction = sankeyJustify;
                    break;
            }
        }
        
        console.log('Using node alignment function:', nodeAlign, nodeAlignFunction);
        
        const sankeyGenerator = sankey()
            .nodeWidth(nodeWidth)
            .nodePadding(nodePadding)
            .nodeAlign(nodeAlignFunction)
            .nodeId((d: any) => d.id)
            .extent([[0, 0], [innerWidth, innerHeight]]);

        // Assign column positions - adjust spacing for smaller screens
        const columnSpacing = width < 1000 ? 0.18 : 0.22;
        const columnPositions = [
            0, 
            innerWidth * columnSpacing, 
            innerWidth * (columnSpacing * 2), 
            innerWidth * (columnSpacing * 3), 
            innerWidth * (columnSpacing * 4)
        ];
            
        // Validate data before creating Sankey layout
        if (!nodes || nodes.length === 0) {
            console.warn('No nodes to display');
            return;
        }

        if (!links || links.length === 0) {
            console.warn('No links to display');
            return;
        }

        // Validate that all link sources and targets are valid
        const validNodeIndices = new Set(nodes.map((_, i) => i));
        const validLinks = links.filter(link => {
            if (typeof link.source !== 'number' || typeof link.target !== 'number') {
                console.warn('Invalid link source or target:', link);
                return false;
            }
            if (!validNodeIndices.has(link.source) || !validNodeIndices.has(link.target)) {
                console.warn('Link references non-existent node:', link);
                return false;
            }
            return true;
        });

        if (validLinks.length === 0) {
            console.warn('No valid links after filtering');
            return;
        }

        console.log('Valid nodes:', nodes.length, 'Valid links:', validLinks.length);

        // Prepare the data for the Sankey layout
        const sankeyData = {
            nodes: nodes.map((node, i) => ({
                ...node,
                x0: columnPositions[node.column],
                x1: columnPositions[node.column] + nodeWidth,
                id: i
            })),
            links: validLinks.map(link => ({
                ...link,
                source: link.source,
                target: link.target
            }))
        };

        // Validate sankey data structure
        if (!sankeyData.nodes || !Array.isArray(sankeyData.nodes) || sankeyData.nodes.length === 0) {
            console.error('Invalid sankey data - no nodes:', sankeyData);
            return;
        }

        if (!sankeyData.links || !Array.isArray(sankeyData.links)) {
            console.error('Invalid sankey data - no links:', sankeyData);
            return;
        }

        console.log('Sankey data prepared:', sankeyData.nodes.length, 'nodes,', sankeyData.links.length, 'links');
        console.log('Sample sankey nodes:', sankeyData.nodes.slice(0, 3));
        console.log('Sample sankey links:', sankeyData.links.slice(0, 3));
        
        // Validate all links have valid source and target indices
        const maxNodeIndex = sankeyData.nodes.length - 1;
        const invalidLinks = sankeyData.links.filter(link => {
            const sourceIndex = typeof link.source === 'number' ? link.source : -1;
            const targetIndex = typeof link.target === 'number' ? link.target : -1;
            return sourceIndex < 0 || sourceIndex > maxNodeIndex || 
                   targetIndex < 0 || targetIndex > maxNodeIndex;
        });
        
        if (invalidLinks.length > 0) {
            console.error('Invalid links found:', invalidLinks);
            console.error('Max node index:', maxNodeIndex);
            return;
        }
        
        // Ensure all nodes have required properties for D3 Sankey
        sankeyData.nodes.forEach((node, index) => {
            if (!node.id && node.id !== 0) {
                node.id = index;
            }
        });
        
        // Convert links to use node objects instead of indices
        const processedLinks = sankeyData.links.map(link => ({
            source: typeof link.source === 'number' ? sankeyData.nodes[link.source] : link.source,
            target: typeof link.target === 'number' ? sankeyData.nodes[link.target] : link.target,
            value: link.value,
            mpp: link.mpp,
            projectName: link.projectName,
            goalName: link.goalName
        }));
        
        // Update sankeyData with processed links
        sankeyData.links = processedLinks;

        // Generate the Sankey layout with error handling
        let sankeyNodes: any[] = [];
        let sankeyLinks: any[] = [];
        try {
            // D3 Sankey generator modifies the input data directly, doesn't return a result
            console.log('Calling sankeyGenerator...');
            console.log('Sankey data before generation:', {
                nodes: sankeyData.nodes.map(n => ({ id: n.id, name: n.name, column: n.column })),
                links: sankeyData.links.map(l => ({ source: l.source, target: l.target, value: l.value }))
            });
            
            sankeyGenerator(sankeyData);
            console.log('Sankey generator completed');
            
            sankeyNodes = sankeyData.nodes;
            sankeyLinks = sankeyData.links;
            console.log('After sankey generation:', sankeyNodes.length, 'nodes,', sankeyLinks.length, 'links');
            
            if (!sankeyNodes || !sankeyLinks) {
                console.error('Sankey generator returned invalid data:', sankeyData);
                return;
            }
        } catch (error) {
            console.error('Error generating Sankey layout:', error);
            console.error('Error details:', error instanceof Error ? error.message : String(error), error instanceof Error ? error.stack : '');
            console.error('Sankey data:', sankeyData);
            
            // Try with even simpler configuration as fallback
            try {
                console.log('Trying fallback sankey configuration...');
                const fallbackGenerator = sankey()
                    .nodeWidth(15)
                    .nodePadding(10)
                    .extent([[0, 0], [innerWidth, innerHeight]]);
                
                fallbackGenerator(sankeyData);
                sankeyNodes = sankeyData.nodes;
                sankeyLinks = sankeyData.links;
                console.log('Fallback sankey generation successful');
            } catch (fallbackError) {
                console.error('Fallback sankey generation also failed:', fallbackError);
                return;
            }
        }

        // Validate sankey results before creating visualization
        if (!sankeyNodes || !Array.isArray(sankeyNodes) || sankeyNodes.length === 0) {
            console.error('No sankey nodes generated');
            return;
        }

        if (!sankeyLinks || !Array.isArray(sankeyLinks) || sankeyLinks.length === 0) {
            console.error('No sankey links generated');
            return;
        }

        console.log('Sankey generated:', sankeyNodes.length, 'nodes,', sankeyLinks.length, 'links');
        console.log('Sample sankeyNodes:', sankeyNodes.slice(0, 3));
        console.log('Sample sankeyLinks:', sankeyLinks.slice(0, 3));

        const headerFontSize = width < 1000 ? '12px' : '14px';
        const headerYOffset = width < 1000 ? -25 : -30;
        const headerEdgePadding = width < 1000 ? 48 : 60;
        const headerGroup = g.append('g').attr('class', 'sankey-column-headers');

        columnTitles.forEach((title, columnIndex) => {
            const columnNodes = sankeyNodes.filter((node: SankeyNodeExtra) => node.column === columnIndex && typeof node.x0 === 'number' && typeof node.x1 === 'number');
            const rawHeaderX = columnNodes.length > 0
                ? d3.mean(columnNodes, (node: SankeyNodeExtra) => ((node.x0 ?? 0) + (node.x1 ?? 0)) / 2)
                : columnPositions[columnIndex] + (nodeWidth / 2);
            const fallbackHeaderX = columnPositions[columnIndex] + (nodeWidth / 2);
            const measuredHeaderX = rawHeaderX ?? fallbackHeaderX;
            const headerX = columnIndex === columnTitles.length - 1
                ? Math.min(measuredHeaderX, innerWidth - headerEdgePadding)
                : measuredHeaderX;

            headerGroup.append('text')
                .attr('x', headerX)
                .attr('y', headerYOffset)
                .attr('text-anchor', 'middle')
                .attr('font-size', headerFontSize)
                .attr('font-weight', 'bold')
                .attr('fill', '#333')
                .text(title);
        });

        // Create color scale for nodes (matching Vue implementation)
        const goalNames = sankeyNodes
            .filter((node: SankeyNodeExtra) => node.column === 0)
            .map((node: SankeyNodeExtra) => node.name);
        const goalColor = d3.scaleOrdinal<string, string>()
            .domain(goalNames)
            .range(colorPalette);
        const fallbackNodeColor = '#64748B';
        const multiGoalNodeColor = '#334155';
        
        // CRITICAL: Always pass ALL links to sankey generator (like both Vue functions)
        // The sankey generator must always see the complete dataset to maintain consistent layout
        const filteredLinks = sankeyLinks;
        
        // Filter links for rendering based on showModernizationProjects (like Vue)
        const linksForRendering = showModernizationProjects 
            ? filteredLinks // Show all links (like Vue FullSankeyChart)
            : filteredLinks.filter(d => d.mpp === "No"); // Only show real links (like Vue SankeyChart)

        const getNodeKey = (node: any) => `${node.column}:${node.name}`;
        const linkKeyLookup = new Map<any, string>();
        linksForRendering.forEach((link: any, index: number) => {
            linkKeyLookup.set(
                link,
                `${index}:${getNodeKey(link.source)}->${getNodeKey(link.target)}:${link.projectName ?? ''}:${link.goalName ?? ''}:${link.mpp ?? ''}`
            );
        });

        const getLinkKey = (link: any) =>
            linkKeyLookup.get(link) ??
            `${getNodeKey(link.source)}->${getNodeKey(link.target)}:${link.projectName ?? ''}:${link.goalName ?? ''}:${link.mpp ?? ''}`;

        const nodeGoalLookup = new Map<string, Set<string>>();
        linksForRendering.forEach((link: any) => {
            const linkGoalName = link.goalName ?? (link.source?.column === 0 ? link.source.name : undefined);
            if (!linkGoalName) return;

            [link.source, link.target].forEach((node: any) => {
                const nodeKey = getNodeKey(node);
                const goals = nodeGoalLookup.get(nodeKey) ?? new Set<string>();
                goals.add(linkGoalName);
                nodeGoalLookup.set(nodeKey, goals);
            });
        });

        const getGoalColor = (goalName?: string) =>
            goalName ? goalColor(goalName) : fallbackNodeColor;

        const getNodeFill = (node: any) => {
            if (node.column === 0) {
                return getGoalColor(node.name);
            }

            const goalNamesForNode = Array.from(nodeGoalLookup.get(getNodeKey(node)) ?? []);
            if (goalNamesForNode.length === 1) {
                return getGoalColor(goalNamesForNode[0]);
            }
            if (goalNamesForNode.length > 1) {
                return multiGoalNodeColor;
            }
            return fallbackNodeColor;
        };

        const getLinkStroke = (link: any, index: number) => {
            if (link.goalName) {
                return getGoalColor(link.goalName);
            }

            switch (linkColor) {
                case 'source':
                    return getNodeFill(link.source);
                case 'target':
                    return getNodeFill(link.target);
                case 'source-target':
                    return `url(#link-gradient-${index})`;
                case 'static':
                default:
                    return fallbackNodeColor;
            }
        };

        const getBaseLinkStrokeWidth = (d: any) => {
            if (d.mpp === "Yes") {
                return showModernizationProjects ? Math.max(1, d.width ?? 1) : 0;
            }
            return Math.max(1, d.width ?? 1);
        };

        const getBaseLinkStrokeOpacity = (d: any) => {
            if (d.mpp === "Yes") {
                return showModernizationProjects ? 0.2 : 0;
            }
            return 0.7;
        };

        const getHighlightedLinkStrokeOpacity = (d: any) => d.mpp === "Yes" ? 0.55 : 0.95;

        const walkConnectedPath = (
            startNode: any,
            direction: 'upstream' | 'downstream',
            nodeKeys: Set<string>,
            linkKeys: Set<string>,
            initialProjectName?: string,
            initialGoalName?: string
        ) => {
            const queue: Array<{ node: any; projectName?: string; goalName?: string }> = [
                { node: startNode, projectName: initialProjectName, goalName: initialGoalName }
            ];
            const getVisitedKey = (node: any, projectName?: string, goalName?: string) =>
                `${direction}:${getNodeKey(node)}:${projectName ?? '*'}:${goalName ?? '*'}`;
            const visited = new Set<string>([getVisitedKey(startNode, initialProjectName, initialGoalName)]);

            while (queue.length > 0) {
                const current = queue.shift();
                const currentNode = current?.node;
                if (!currentNode) continue;

                const currentProjectName = current?.projectName;
                const currentGoalName = current?.goalName;
                const currentNodeKey = getNodeKey(currentNode);

                linksForRendering.forEach((link: any) => {
                    const sourceKey = getNodeKey(link.source);
                    const targetKey = getNodeKey(link.target);
                    const isConnected = direction === 'upstream'
                        ? targetKey === currentNodeKey
                        : sourceKey === currentNodeKey;

                    if (!isConnected) return;
                    if (currentProjectName && link.projectName !== currentProjectName) return;
                    if (currentGoalName && link.goalName !== currentGoalName) return;

                    const nextNode = direction === 'upstream' ? link.source : link.target;
                    const nextKey = direction === 'upstream' ? sourceKey : targetKey;
                    const nextProjectName = currentProjectName ?? link.projectName;
                    const nextGoalName = currentGoalName ?? link.goalName;

                    linkKeys.add(getLinkKey(link));
                    nodeKeys.add(nextKey);

                    const visitedKey = getVisitedKey(nextNode, nextProjectName, nextGoalName);
                    if (!visited.has(visitedKey)) {
                        visited.add(visitedKey);
                        queue.push({ node: nextNode, projectName: nextProjectName, goalName: nextGoalName });
                    }
                });
            }
        };

        const collectNodePath = (startNode: any) => {
            const nodeKeys = new Set<string>([getNodeKey(startNode)]);
            const linkKeys = new Set<string>();
            const projectName = startNode.column === 1 ? startNode.name : undefined;
            const goalName = startNode.column === 0 ? startNode.name : undefined;

            walkConnectedPath(startNode, 'upstream', nodeKeys, linkKeys, projectName, goalName);
            walkConnectedPath(startNode, 'downstream', nodeKeys, linkKeys, projectName, goalName);

            return { nodeKeys, linkKeys };
        };

        const collectLinkPath = (startLink: any) => {
            const nodeKeys = new Set<string>([
                getNodeKey(startLink.source),
                getNodeKey(startLink.target)
            ]);
            const linkKeys = new Set<string>([getLinkKey(startLink)]);
            const projectName = startLink.projectName;
            const goalName = startLink.goalName;

            walkConnectedPath(startLink.source, 'upstream', nodeKeys, linkKeys, projectName, goalName);
            walkConnectedPath(startLink.target, 'downstream', nodeKeys, linkKeys, projectName, goalName);

            return { nodeKeys, linkKeys };
        };

        const applyPathHighlight = (path: { nodeKeys: Set<string>; linkKeys: Set<string> }) => {
            setHighlightedPaths(new Set(path.linkKeys.size > 0 ? path.linkKeys : path.nodeKeys));

            g.selectAll<SVGPathElement, any>('.sankey-link')
                .style('opacity', null)
                .style('filter', (d: any) =>
                    path.linkKeys.has(getLinkKey(d)) ? 'drop-shadow(0 0 4px rgba(15, 42, 79, 0.35))' : 'none'
                )
                .attr('stroke-opacity', (d: any) =>
                    path.linkKeys.has(getLinkKey(d)) ? getHighlightedLinkStrokeOpacity(d) : 0.08
                )
                .attr('stroke-width', (d: any) => {
                    const baseWidth = getBaseLinkStrokeWidth(d);
                    if (!path.linkKeys.has(getLinkKey(d))) {
                        return baseWidth;
                    }
                    return d.mpp === "Yes" ? baseWidth : Math.max(baseWidth + 2, baseWidth * 1.35);
                });

            g.selectAll<SVGGElement, any>('.sankey-node')
                .style('opacity', (d: any) => path.nodeKeys.has(getNodeKey(d)) ? 1 : 0.16)
                .style('filter', (d: any) =>
                    path.nodeKeys.has(getNodeKey(d)) ? 'drop-shadow(0 0 5px rgba(15, 42, 79, 0.25))' : 'none'
                );
        };
        
        const linkGroup = g.append('g')
            .attr('fill', 'none')
            .attr('stroke-opacity', 0.5)
            .selectAll('g')
            .data(linksForRendering)
            .enter()
            .append('g')
            .style('mix-blend-mode', 'multiply');

        // Create gradients for source-target links
        if (linkColor === 'source-target') {
            svg.append('defs');
            linkGroup.append('linearGradient')
                .attr('id', (d: any, i: number) => `link-gradient-${i}`)
                .attr('gradientUnits', 'userSpaceOnUse')
                .attr('x1', (d: any) => d.source.x1)
                .attr('x2', (d: any) => d.target.x0)
                .call(gradient => gradient.append('stop')
                    .attr('offset', '0%')
                    .attr('stop-color', (d: any) => getNodeFill(d.source)))
                .call(gradient => gradient.append('stop')
                    .attr('offset', '100%')
                    .attr('stop-color', (d: any) => getNodeFill(d.target)));
        }

        // Create the actual link paths
        linkGroup.append('path')
            .attr('d', sankeyLinkHorizontal())
            .style('transition', 'opacity 0.3s ease, stroke-width 0.3s ease')
            .style('cursor', 'pointer')
            .attr('stroke', getLinkStroke)
            .attr('stroke-opacity', getBaseLinkStrokeOpacity)
            .attr('stroke-dasharray', (d: any) => {
                // Dashed line for modernization projects when shown
                if (showModernizationProjects && d.mpp === "Yes") {
                    return '2,2';
                }
                return null;
            })
            .attr('stroke-width', getBaseLinkStrokeWidth)
            .attr('class', (d: any, i: number) => `sankey-link sankey-link-${i}`)
            .attr('data-index', (d: any, i: number) => i)
            .on('click', function(event: any, d: any) {
                event.stopPropagation();
                applyPathHighlight(collectLinkPath(d));
            });

        // Create tooltip div with responsive styling
        const tooltip = d3.select("body").append("div")
            .attr("class", "tooltip")
            .style("position", "absolute")
            .style("background-color", "white")
            .style("border", "1px solid #ddd")
            .style("border-radius", "4px")
            .style("padding", width < 1000 ? "6px" : "8px")
            .style("pointer-events", "none")
            .style("opacity", 0)
            .style("z-index", 1000)
            .style("font-size", width < 1000 ? "12px" : "13px")
            .style("max-width", "200px")
            .style("word-wrap", "break-word");

        // CRITICAL: Always pass ALL nodes to sankey generator (like both Vue functions)
        // The sankey generator must always see the complete dataset to maintain consistent layout
        const filteredNodes = sankeyNodes;
        
        // Filter nodes for rendering based on showModernizationProjects (like Vue)
        const nodesForRendering = showModernizationProjects 
            ? filteredNodes // Show all nodes (like Vue FullSankeyChart)
            : filteredNodes.filter(d =>
                (d.sourceLinks && d.sourceLinks.some((link: any) => link.mpp === "No")) ||
                (d.targetLinks && d.targetLinks.some((link: any) => link.mpp === "No"))
            ); // Only show nodes connected to real links (like Vue SankeyChart)

        const getNodeStroke = (d: any) => {
            const isModernizationNode = modernizationProjects.has(d.name);
            if (showModernizationProjects && isModernizationNode && d.column === 1) {
                return '#D84315';
            }
            return '#000';
        };

        const getNodeStrokeWidth = (d: any) => {
            const isModernizationNode = modernizationProjects.has(d.name);
            if (showModernizationProjects && isModernizationNode && d.column === 1) {
                return 2;
            }
            return 1;
        };
        
        const nodeGroups = g.append('g')
            .attr('class', 'sankey-nodes')
            .selectAll('g')
            .data(nodesForRendering)
            .enter()
            .append('g')
            .attr('class', 'sankey-node')
            .attr('data-node-key', (d: any) => getNodeKey(d))
            .style('cursor', 'pointer')
            .style('transition', 'opacity 0.3s ease, filter 0.3s ease')
            .on('click', function(event: any, d: any) {
                event.stopPropagation();
                applyPathHighlight(collectNodePath(d));
            });

        // Add rectangles for nodes
        nodeGroups.append('rect')
            .attr('x', (d: any) => d.x0)
            .attr('y', (d: any) => d.y0)
            .attr('height', (d: any) => d.y1 - d.y0)
            .attr('width', (d: any) => d.x1 - d.x0)
            .style('transition', 'opacity 0.3s ease')
            .attr('fill', getNodeFill)
            .attr('stroke', getNodeStroke)
            .attr('stroke-width', getNodeStrokeWidth)
            // Add hover functionality
            .on('mouseover', function(this: any, event: any, d: any) {
                const columnName = columnTitles[d.column];
                
                // Show tooltip
                tooltip.transition()
                    .duration(200)
                    .style("opacity", .9);
                tooltip.html(`<strong>${columnName}:</strong> ${d.name}`)
                    .style("left", (event.pageX + 10) + "px")
                    .style("top", (event.pageY - 28) + "px")
                    .style("font-size", "13px");
                
                // Highlight the node
                d3.select(this)
                    .attr('stroke', '#000000')
                    .attr('stroke-width', 2);
            })
            .on('mouseout', function(this: any, event: any, d: any) {
                // Hide tooltip
                tooltip.transition()
                    .duration(500)
                    .style("opacity", 0);
                
                // Remove highlight
                d3.select(this)
                    .attr('stroke', getNodeStroke(d))
                    .attr('stroke-width', getNodeStrokeWidth(d));
            });

        // Add labels to nodes with responsive settings - match Vue behavior
        const labelFontSize = width < 1000 ? '11px' : '13px';
        const labelOffset = width < 1000 ? 4 : 6;
        
        nodeGroups.append('text')
            .attr('x', (d: any) => d.x0 < innerWidth / 2 ? d.x1 + labelOffset : d.x0 - labelOffset)
            .attr('y', (d: any) => (d.y1 + d.y0) / 2)
            .attr('dy', '0.35em')
            .attr('text-anchor', (d: any) => d.x0 < innerWidth / 2 ? 'start' : 'end')
            .text((d: any) => d.name)
            .attr('font-size', labelFontSize)
            .attr('font-family', 'Arial, sans-serif')
            .style('pointer-events', 'none');
    }, [dimensions, linkColor, nodeAlign, resetHighlighting, colorPalette, view, controlsVariant]);

    // Add keyboard shortcut for resetting highlighting
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && highlightedPaths.size > 0) {
                resetHighlighting();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [highlightedPaths.size, resetHighlighting]);

    // Add click outside handler for resetting highlighting
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (highlightedPaths.size > 0) {
                // Check if click is outside the SVG area
                const svgElement = svgRef.current;
                if (svgElement && !svgElement.contains(event.target as Node)) {
                    // Check if click is also outside the container
                    const containerElement = containerRef.current;
                    if (containerElement && !containerElement.contains(event.target as Node)) {
                
                        resetHighlighting();
                    }
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [highlightedPaths.size, resetHighlighting]);

    useEffect(() => {
        if (!svgRef.current) return;

        // Clear any existing SVG content
        d3.select(svgRef.current).selectAll("*").remove();

        // Reset highlighting when data changes
        resetHighlighting();

        if (!projects || projects.length === 0 || !hasDisplayableData) {
            const svg = d3.select(svgRef.current);
            svg.append("text")
                .attr("x", dimensions.width / 2)
                .attr("y", dimensions.height / 2)
                .attr("text-anchor", "middle")
                .attr("font-size", "16px")
                .attr("fill", "#666")
                .text("No data available");
            return;
        }

        // Process data to create nodes and links
        const { nodes, links, modernizationProjects } = processData(projects);

        // Only create the Sankey diagram if we have valid data
        if (nodes.length > 0 && links.length > 0) {
            createSankeyDiagram(nodes, links, showModernizationProjects, modernizationProjects);
        } else {
            // Display a message when there's no data to show
            const svg = d3.select(svgRef.current);
            svg.append("text")
                .attr("x", dimensions.width / 2)
                .attr("y", dimensions.height / 2)
                .attr("text-anchor", "middle")
                .attr("font-size", "16px")
                .attr("fill", "#666")
                .text("No data available");
        }
    }, [projects, showModernizationProjects, hasDisplayableData, dimensions, linkColor, nodeAlign, view, createSankeyDiagram, resetHighlighting]);

    // Process the data to create nodes and links for the Sankey diagram
  const processData = (projects: ProjectItem[]) => {
    
    
    // Keep track of modernization projects
    const modernizationProjects = new Set<string>();
    
    // First pass: identify all modernization projects
    projects.forEach(project => {
      const isModernizationProject = project.modernization_plan_project === 'Yes' || 
                                    (project as any).Modernization_plan_project === 'Yes';
      
      if (isModernizationProject && project.name) {
        modernizationProjects.add(project.name);
      }
    });
    
    // CRITICAL: Always process ALL projects to maintain consistent sankey layout
    // Filtering will be done during rendering, not during data processing
    const filteredProjects = projects;
    
    // If there are no projects, return empty nodes and links
    if (filteredProjects.length === 0) {
      console.log('No projects after filtering');
      return { nodes: [], links: [], modernizationProjects: new Set<string>() };
    }
    
    
    
    // Sort projects alphabetically by name
    const sortedProjects = [...filteredProjects].sort((a, b) => 
      a.name.localeCompare(b.name)
    );
    
    // First, collect all nodes by column to ensure proper ordering
    const nodesByColumn: { [key: number]: string[] } = {
      0: [], // Goals
      1: [], // Projects
      2: [], // First part
      3: [], // Second part
      4: []  // Third part
    };
    
    // Add all project names to column 1 (sorted)
    sortedProjects.forEach(project => {
      if (project.name) {
        nodesByColumn[1].push(project.name);
      }
      
      // Collect goals
      if (project.goals && Array.isArray(project.goals) && project.goals.length > 0) {
        project.goals.forEach(goal => {
          const goalName = goal?.goal || 'Unknown Goal';
          if (!nodesByColumn[0].includes(goalName)) {
            nodesByColumn[0].push(goalName);
          }
        });
      }
      
      // Collect presidio areas
      if (project.presidio_areas && Array.isArray(project.presidio_areas) && project.presidio_areas.length > 0) {
        console.log(`Processing presidio areas for project ${project.name}:`, project.presidio_areas);
        project.presidio_areas.forEach(area => {
          if (typeof area === 'string') {
            const parts = area.split(' - ');
            console.log(`Area "${area}" split into ${parts.length} parts:`, parts);
            
            if (parts.length >= 3) {
              const part1 = parts[0].trim();
              const part2 = parts[1].trim();
              const part3 = parts[2].trim();
              
              if (!nodesByColumn[2].includes(part1)) {
                nodesByColumn[2].push(part1);
              }
              
              if (!nodesByColumn[3].includes(part2)) {
                nodesByColumn[3].push(part2);
              }
              
              if (!nodesByColumn[4].includes(part3)) {
                nodesByColumn[4].push(part3);
              }
            } else {
              console.warn(`Presidio area "${area}" doesn't have 3 parts, skipping`);
            }
          }
        });
      }
    });
    
    // Vue behavior: Don't add "No Presidio Areas" node
    // Projects without presidio areas simply won't have connections to presidio area columns
    
    // Sort each column alphabetically
    for (const column in nodesByColumn) {
      // Make sure we're sorting case-insensitive
      nodesByColumn[column].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }
    
    // Now create nodes and links with the sorted order
    const nodes: SankeyNodeExtra[] = [];
    const links: SankeyLinkExtra[] = [];
    const nodeMap: Map<string, number> = new Map();
    
    // Create all nodes first in the sorted order
    for (const column in nodesByColumn) {
      const columnIndex = parseInt(column);
      nodesByColumn[columnIndex].forEach(nodeName => {
        const index = nodes.length;
        nodes.push({ id: index, name: nodeName, column: columnIndex });
        nodeMap.set(`${nodeName}-${columnIndex}`, index);
      });
    }
    
    // Helper function to get node index
    const getNodeIndex = (name: string, column: number): number | null => {
      const key = `${name}-${column}`;
      const index = nodeMap.get(key);
      if (index === undefined) {
        console.warn(`Node not found: ${name} in column ${column}`);
        return null; // Return null if node not found
      }
      return index;
    };


    
    // Process projects and create links
    sortedProjects.forEach(project => {
      if (!project.name) return; // Skip projects without names
      
      const projectNodeIndex = getNodeIndex(project.name, 1);
      if (projectNodeIndex === null) return; // Skip if project node doesn't exist
      
      // Check if this is a modernization project
      const isModernizationProject = modernizationProjects.has(project.name);
      const projectGoalNames = project.goals && Array.isArray(project.goals)
        ? project.goals.map(goal => goal?.goal || 'Unknown Goal')
        : [];

      // Create links from goals to this project
      if (projectGoalNames.length > 0) {
        projectGoalNames.forEach(goalName => {
          const goalNodeIndex = getNodeIndex(goalName, 0);
          if (goalNodeIndex !== null) {
            links.push({
              source: goalNodeIndex,
              target: projectNodeIndex,
              value: 1,
              mpp: isModernizationProject ? "Yes" : "No",
              projectName: project.name,
              goalName
            });
          }
        });
      }

      // Process presidio areas and create links from project to areas
      if (project.presidio_areas && Array.isArray(project.presidio_areas) && project.presidio_areas.length > 0) {
        const flowGoalNames = projectGoalNames.length > 0 ? projectGoalNames : [undefined];
        const segmentValue = 1 / project.presidio_areas.length;

        project.presidio_areas.forEach(area => {
          if (typeof area === 'string') {
            const parts = area.split(' - ');

            if (parts.length >= 3) {
              const part1 = parts[0].trim();
              const part2 = parts[1].trim();
              const part3 = parts[2].trim();

              const part1NodeIndex = getNodeIndex(part1, 2);
              const part2NodeIndex = getNodeIndex(part2, 3);
              const part3NodeIndex = getNodeIndex(part3, 4);

              // Only create links if all nodes exist
              if (part1NodeIndex !== null && part2NodeIndex !== null && part3NodeIndex !== null) {
                const isPathFromModernizationProject = modernizationProjects.has(project.name);

                flowGoalNames.forEach(goalName => {
                  links.push({
                    source: projectNodeIndex,
                    target: part1NodeIndex,
                    value: segmentValue,
                    mpp: isPathFromModernizationProject ? "Yes" : "No",
                    projectName: project.name,
                    goalName
                  });

                  links.push({
                    source: part1NodeIndex,
                    target: part2NodeIndex,
                    value: segmentValue,
                    mpp: isPathFromModernizationProject ? "Yes" : "No",
                    projectName: project.name,
                    goalName
                  });

                  links.push({
                    source: part2NodeIndex,
                    target: part3NodeIndex,
                    value: segmentValue,
                    mpp: isPathFromModernizationProject ? "Yes" : "No",
                    projectName: project.name,
                    goalName
                  });
                });
              }
            }
          }
        });
      }
      // Vue behavior: Projects without presidio areas don't create any links to presidio area columns
    });
        
        return { nodes, links, modernizationProjects };
    };

    return (
        <div 
            ref={containerRef} 
            className={`w-full h-full border rounded-lg shadow-sm p-4 ${presentationMode ? 'overflow-hidden' : 'overflow-auto'} ${highlightedPaths.size > 0 ? 'cursor-pointer' : ''}`}
            onClick={(e) => {
                // Reset highlighting when clicking on empty space in the container
                if (e.target === e.currentTarget && highlightedPaths.size > 0) {
                    console.log('Click on container background, resetting highlighting');
                    resetHighlighting();
                }
            }}
            title={highlightedPaths.size > 0 ? "Click here to reset highlighting" : ""}
        >

            {!hideHeader && (
                <div className='flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6'>
                    <h1 className="text-xl sm:text-2xl font-bold italic text-[#0F2A4F] mb-4 sm:mb-0">{customTitle ?? 'Project Output'}</h1>
                    <div className="flex items-center space-x-4">
                        {headerAction && hasDisplayableData ? headerAction : null}
                        {highlightedPaths.size > 0 && (
                            <button
                                onClick={resetHighlighting}
                                className="px-3 py-1 text-sm bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-md transition-colors"
                            >
                                Reset Highlighting
                            </button>
                        )}
                    </div>
                </div>
            )}
            {!hideDescription && (
            <div className='text-zinc-500 font-normal pt-4 pb-8 text-sm sm:text-base'>
                <p>This Sankey diagram shows {customerName}'s internal systems. It maps how legacy systems support departments, modernized through cloud platforms and AI, impacting efficiency and innovation.</p>
            </div>
            )}

            {!hideControls && controlsVariant === 'mapButtons' && (
                <div className="mb-4 flex flex-wrap gap-3">
                    {!hideCheckbox && (
                        <button
                            type="button"
                            onClick={toggleModernizationProjects}
                            className={`rounded-2xl px-4 py-2 ${
                                showModernizationProjects ? 'bg-blue-600 text-white' : 'bg-white border border-gray-300 text-gray-700'
                            }`}
                        >
                            Show Modernization Plan Projects
                        </button>
                    )}
                    <button type="button" onClick={resetHighlighting} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                        Clear Highlights
                    </button>
                    <button type="button" onClick={() => zoomBy(1.12)} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                        Zoom In
                    </button>
                    <button type="button" onClick={() => zoomBy(0.9)} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                        Zoom Out
                    </button>
                    <button type="button" onClick={resetView} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                        Reset View
                    </button>
                    <button type="button" onClick={fitToView} className="rounded-2xl px-4 py-2 bg-white border border-gray-300 text-gray-700">
                        Fit to View
                    </button>
                </div>
            )}
            
            <div className="w-full overflow-x-auto" data-output-graph-container="true">
                <svg 
                    ref={svgRef} 
                    data-output-graph-svg="true"
                    style={{ 
                        width: '100%', 
                        height: `${dimensions.height}px`,
                        minWidth: `${effectiveMapMinWidth}px`
                    }}
                ></svg>
            </div>
            
            {!hideControls && controlsVariant === 'default' && (
                    <div className="mt-6 p-4 bg-gray-50 rounded-lg border">
                        <div className="flex flex-wrap items-center gap-4 justify-center">
                            {/* Show Modernization Projects */}
                            {!hideCheckbox && (
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        id="showModernizationProjectsControl"
                                        checked={showModernizationProjects}
                                        onChange={(e) => setInternalShowModernization(e.target.checked)}
                                        className="mr-2"
                                    />
                                    <label htmlFor="showModernizationProjectsControl" className="text-sm font-medium text-gray-700">
                                        Show Modernization
                                    </label>
                                </div>
                            )}
                            
                            {/* Node Alignment */}
                            <div className="flex items-center">
                                <label htmlFor="nodeAlign" className="text-sm font-medium text-gray-700 mr-2">
                                    Node Align:
                                </label>
                                <select
                                    id="nodeAlign"
                                    value={nodeAlign}
                                    onChange={(e) => setNodeAlign(e.target.value as any)}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                    <option value="justify">Justify</option>
                                    <option value="left">Left</option>
                                    <option value="right">Right</option>
                                    <option value="center">Center</option>
                                </select>
                            </div>
                            
                            {/* Link Color */}
                            <div className="flex items-center">
                                <label htmlFor="linkColor" className="text-sm font-medium text-gray-700 mr-2">
                                    Link Color:
                                </label>
                                <select
                                    id="linkColor"
                                    value={linkColor}
                                    onChange={(e) => setLinkColor(e.target.value as any)}
                                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                                >
                                    <option value="source">Source</option>
                                    <option value="target">Target</option>
                                    <option value="source-target">Source-Target</option>
                                    <option value="static">Static</option>
                                </select>
                            </div>
                        </div>
                    </div>
            )}
        </div>
    );
};

export default OutputGraph;
